import { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  Calculator,
  Calendar,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock,
  FileText,
  Languages,
  Leaf,
  Lightbulb,
  Monitor,
  Palette,
  type LucideIcon,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { EXAM_TYPE_LABEL } from '@/features/teacher/exams/constants';
import { instituteToday } from '@/features/teacher/utils';
import { formatDate, formatTimeRange } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { FamilyExamPaper } from '@/types/student';

export interface UpcomingExamsScheduleProps {
  /** The papers still to sit, earliest first, as the server sent them. */
  exams: FamilyExamPaper[];
  /** The institute's today, `YYYY-MM-DD` — what the server filtered from. Null until loaded. */
  today: string | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  /** What the empty state says — a parent and a child are told different things. */
  emptyDescription: string;
  /** Labels each timed paper Morning or Afternoon under its time. Off by default. */
  showSession?: boolean;
}

/** How far ahead the "Upcoming Exams" tile counts. */
const SOON_DAYS = 30;
const DAY_MS = 86_400_000;
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** A subject's badge colours. Cycled by subject, so one subject keeps one colour on the page. */
const SUBJECT_TONES = [
  { badge: 'bg-purple-500/15 border-purple-500/30 text-purple-700 dark:text-purple-300', icon: 'text-purple-600 dark:text-purple-400' },
  { badge: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-700 dark:text-emerald-300', icon: 'text-emerald-600 dark:text-emerald-400' },
  { badge: 'bg-sky-500/15 border-sky-500/30 text-sky-700 dark:text-sky-300', icon: 'text-sky-600 dark:text-sky-400' },
  { badge: 'bg-amber-500/15 border-amber-500/30 text-amber-700 dark:text-amber-300', icon: 'text-amber-600 dark:text-amber-400' },
  { badge: 'bg-rose-500/15 border-rose-500/30 text-rose-700 dark:text-rose-300', icon: 'text-rose-600 dark:text-rose-400' },
  { badge: 'bg-indigo-500/15 border-indigo-500/30 text-indigo-700 dark:text-indigo-300', icon: 'text-indigo-600 dark:text-indigo-400' },
] as const;

/** A picture for a subject, read from its name — the institute names its own subjects. */
function subjectIcon(name: string): LucideIcon {
  const n = name.toLowerCase();
  if (/math|algebra|geometry|account/.test(n)) return Calculator;
  if (/english|hindi|telugu|tamil|kannada|marathi|sanskrit|urdu|french|language/.test(n)) return Languages;
  if (/science|biology|chemistry|physics|environment|evs/.test(n)) return Leaf;
  if (/computer|it\b|coding|informatics/.test(n)) return Monitor;
  if (/art|drawing|music|craft/.test(n)) return Palette;
  return BookOpen;
}

/** `YYYY-MM-DD` as a UTC midnight, so day arithmetic never crosses a time zone. */
function utcDay(day: string): number {
  return Date.parse(`${day}T00:00:00Z`);
}

function daysBetween(from: string, to: string): number {
  return Math.round((utcDay(to) - utcDay(from)) / DAY_MS);
}

function weekdayShort(day: string): string {
  return WEEKDAY_SHORT[new Date(utcDay(day)).getUTCDay()];
}

function weekdayLong(day: string): string {
  return new Intl.DateTimeFormat('en-IN', { weekday: 'long', timeZone: 'UTC' }).format(new Date(utcDay(day)));
}

function daysLeftLabel(days: number): string {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `${days} Days Left`;
}

/**
 * Which half of the school day a paper is sat in, read from when it starts.
 * Null for a paper with no recorded time — it has no half to belong to. Times
 * are "HH:MM", 24-hour and zero-padded, so a string comparison orders them.
 */
function paperSession(paper: FamilyExamPaper): 'Morning' | 'Afternoon' | null {
  if (!paper.startTime) return null;
  return paper.startTime < '12:00' ? 'Morning' : 'Afternoon';
}

function paperTime(paper: FamilyExamPaper): string {
  return formatTimeRange(paper.startTime, paper.endTime, 'Time not set');
}

/** iCalendar text values escape `\`, `;`, `,` and newlines (RFC 5545 §3.3.11). */
function icsText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

/**
 * The schedule as an `.ics` file. A timed paper is written in local (floating)
 * time — the school's clock is the family's clock — and an untimed one as an
 * all-day entry on its date.
 */
function downloadCalendar(papers: FamilyExamPaper[]) {
  const compact = (day: string) => day.replace(/-/g, '');
  const clock = (time: string) => `${time.replace(':', '')}00`;
  const nextDay = (day: string) => new Date(utcDay(day) + DAY_MS).toISOString().slice(0, 10);

  const events = papers.map((paper) => {
    const when = paper.startTime
      ? [
          `DTSTART:${compact(paper.examDate)}T${clock(paper.startTime)}`,
          `DTEND:${compact(paper.examDate)}T${clock(paper.endTime ?? paper.startTime)}`,
        ]
      : [`DTSTART;VALUE=DATE:${compact(paper.examDate)}`, `DTEND;VALUE=DATE:${compact(nextDay(paper.examDate))}`];

    return [
      'BEGIN:VEVENT',
      `UID:${paper.uuid}`,
      `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').slice(0, 15)}Z`,
      ...when,
      `SUMMARY:${icsText(`${paper.name} - ${paper.subject.name}`)}`,
      `DESCRIPTION:${icsText(`${EXAM_TYPE_LABEL[paper.examType]} · out of ${paper.maxMarks} marks`)}`,
      'END:VEVENT',
    ].join('\r\n');
  });

  const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//School//ExamSchedule//EN', ...events, 'END:VCALENDAR'].join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'exam-schedule.ics';
  link.click();
  URL.revokeObjectURL(url);
}

/** One month's grid, Sunday first, padded with the neighbouring months' days. */
function monthGrid(year: number, month: number): { day: string; inMonth: boolean }[] {
  const first = Date.UTC(year, month, 1);
  const start = first - new Date(first).getUTCDay() * DAY_MS;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells = Math.ceil((new Date(first).getUTCDay() + daysInMonth) / 7) * 7;

  return Array.from({ length: cells }, (_, index) => {
    const date = new Date(start + index * DAY_MS);
    return { day: date.toISOString().slice(0, 10), inMonth: date.getUTCMonth() === month };
  });
}

/**
 * The papers a child is still to sit — the parent's Upcoming Exams tab and the
 * student's own exams page, drawn by one component.
 *
 * Every number here is counted from the published exam timetable — the same
 * read the class timetable uses, so a paper is on the same day in both places.
 * "Days left" counts from the institute's today, which the server sends, not
 * from the browser's clock. The caller owns the fetch.
 */
export function UpcomingExamsSchedule({
  exams,
  today: serverToday,
  isLoading,
  error,
  onRetry,
  emptyDescription,
  showSession = false,
}: UpcomingExamsScheduleProps) {
  const today = serverToday ?? instituteToday();
  const papers = exams;

  const [examFilter, setExamFilter] = useState('all');
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  // A day picked on the calendar marks every paper sat that day in the table —
  // a morning and an afternoon paper are both that day's. Picking a row picks
  // that one paper, and clears it.
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [detailPaper, setDetailPaper] = useState<FamilyExamPaper | null>(null);
  const [viewMonth, setViewMonth] = useState<{ year: number; month: number } | null>(null);

  /** "Unit Test I", "Half Yearly" — the institute's own exam names, in date order. */
  const examNames = useMemo(() => [...new Set(papers.map((paper) => paper.name))], [papers]);

  const filteredPapers = useMemo(
    () => (examFilter === 'all' ? papers : papers.filter((paper) => paper.name === examFilter)),
    [papers, examFilter],
  );

  const toneBySubject = useMemo(() => {
    const subjects = [...new Set(papers.map((paper) => paper.subject.uuid))];
    return new Map(subjects.map((uuid, index) => [uuid, SUBJECT_TONES[index % SUBJECT_TONES.length]]));
  }, [papers]);

  const nextPaper = papers[0] ?? null;
  const selectedPaper = papers.find((paper) => paper.uuid === selectedUuid) ?? nextPaper;

  // The calendar opens on the month of the next paper, or this month with none.
  useEffect(() => {
    const anchor = nextPaper?.examDate ?? today;
    setViewMonth({ year: Number(anchor.slice(0, 4)), month: Number(anchor.slice(5, 7)) - 1 });
  }, [nextPaper?.examDate, today]);

  const papersByDay = useMemo(() => {
    const byDay = new Map<string, FamilyExamPaper[]>();
    for (const paper of papers) byDay.set(paper.examDate, [...(byDay.get(paper.examDate) ?? []), paper]);
    return byDay;
  }, [papers]);

  const soonCount = papers.filter((paper) => daysBetween(today, paper.examDate) <= SOON_DAYS).length;
  const subjectCount = toneBySubject.size;
  const examDayCount = papersByDay.size;
  const daysUntilFirst = nextPaper ? daysBetween(today, nextPaper.examDate) : null;
  const isTodayOrTomorrow = daysUntilFirst !== null && daysUntilFirst <= 1;
  const firstExamTitle = isTodayOrTomorrow ? 'First Exam' : 'First Exam In';
  const examDaysTitle = examDayCount === 1 ? 'Exam Day' : 'Exam Days';
  const examDaysCaption = examDayCount === 1 ? 'Day with a paper' : 'Days with a paper';

  function shiftMonth(by: number) {
    setViewMonth((current) => {
      if (!current) return current;
      const date = new Date(Date.UTC(current.year, current.month + by, 1));
      return { year: date.getUTCFullYear(), month: date.getUTCMonth() };
    });
  }

  if (isLoading) return <SkeletonLines count={8} />;

  if (error) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert tone="critical" title="Could not load the exam schedule">
          {error.message}
        </Alert>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }

  if (papers.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon={<Calendar className="h-5 w-5" />}
          title="No upcoming exams"
          description={emptyDescription}
        />
      </div>
    );
  }

  const monthLabel = viewMonth
    ? new Intl.DateTimeFormat('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
        new Date(Date.UTC(viewMonth.year, viewMonth.month, 1)),
      )
    : '';
  const calendarDays = viewMonth ? monthGrid(viewMonth.year, viewMonth.month) : [];
  const SelectedIcon = selectedPaper ? subjectIcon(selectedPaper.subject.name) : BookOpen;
  const selectedTone = selectedPaper ? toneBySubject.get(selectedPaper.subject.uuid) : undefined;

  return (
    <div className="flex flex-col gap-5">
      {/* 1. TOP STAT CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatTile
          title="Upcoming Exams"
          value={String(soonCount)}
          caption={`Next ${SOON_DAYS} Days`}
          icon={Calendar}
          frame="border-purple-200/90 dark:border-slate-800 bg-purple-50/90 dark:bg-slate-900"
          titleClass="text-purple-800 dark:text-purple-400"
          valueClass="text-purple-600 dark:text-purple-400"
          captionClass="text-purple-600/90 dark:text-slate-400"
          iconClass="bg-violet-600"
        />
        <StatTile
          title="Subjects"
          value={String(subjectCount)}
          caption={`${papers.length} ${papers.length === 1 ? 'paper' : 'papers'} scheduled`}
          icon={FileText}
          frame="border-fuchsia-200/90 dark:border-slate-800 bg-fuchsia-50/90 dark:bg-slate-900"
          titleClass="text-fuchsia-800 dark:text-fuchsia-400"
          valueClass="text-fuchsia-600 dark:text-fuchsia-400"
          captionClass="text-fuchsia-600/90 dark:text-slate-400"
          iconClass="bg-purple-500"
        />
        <StatTile
          title={firstExamTitle}
          value={nextPaper ? daysLeftLabel(daysBetween(today, nextPaper.examDate)).replace(' Left', '') : '—'}
          caption={nextPaper ? formatDate(nextPaper.examDate) : ''}
          icon={Clock}
          frame="border-cyan-200/90 dark:border-slate-800 bg-cyan-50/90 dark:bg-slate-900"
          titleClass="text-cyan-800 dark:text-cyan-400"
          valueClass="text-cyan-600 dark:text-cyan-400"
          captionClass="text-cyan-600/90 dark:text-slate-400"
          iconClass="bg-cyan-600"
        />
        <StatTile
          title={examDaysTitle}
          value={String(examDayCount)}
          caption={examDaysCaption}
          icon={ClipboardCheck}
          frame="border-emerald-200/90 dark:border-slate-800 bg-emerald-50/90 dark:bg-slate-900"
          titleClass="text-emerald-800 dark:text-emerald-400"
          valueClass="text-emerald-600 dark:text-emerald-400"
          captionClass="text-emerald-700/90 dark:text-slate-400"
          iconClass="bg-emerald-600"
        />
      </div>

      {/* 2. MAIN TWO-COLUMN CONTENT */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* --- LEFT COLUMN: UPCOMING EXAMS SCHEDULE TABLE --- */}
        <div className="lg:col-span-8 flex flex-col gap-4">
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-line">
              <h3 className="text-base font-bold text-ink">Upcoming Exams Schedule</h3>

              <button
                type="button"
                onClick={() => downloadCalendar(papers)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-accent-line text-accent hover:bg-accent-soft text-xs sm:text-sm font-bold shadow-2xs transition-colors cursor-pointer"
              >
                <CalendarPlus className="h-4 w-4 text-accent" aria-hidden />
                <span>Add to Calendar</span>
              </button>
            </div>

            {/* Filter Pills — one per exam the school has published */}
            {examNames.length > 1 && (
              <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by exam">
                {[{ key: 'all', label: 'All', count: papers.length }, ...examNames.map((name) => ({
                  key: name,
                  label: name,
                  count: papers.filter((paper) => paper.name === name).length,
                }))].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    aria-pressed={examFilter === tab.key}
                    onClick={() => setExamFilter(tab.key)}
                    className={cn(
                      'px-3.5 py-1.5 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer',
                      examFilter === tab.key
                        ? 'bg-accent text-white shadow-2xs'
                        : 'bg-surface-2 text-ink-2 hover:text-ink hover:bg-surface border border-line',
                    )}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>
            )}

            {/* Schedule Table */}
            <div className="overflow-x-auto rounded-xl border border-line">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-line bg-surface-2">
                    <th scope="col" className="py-2.5 px-3 text-center w-10 text-xs font-bold text-ink-3">#</th>
                    <th scope="col" className="py-2.5 px-3 text-sm font-bold text-ink">Exam Name</th>
                    <th scope="col" className="py-2.5 px-3 text-sm font-bold text-ink">Subject</th>
                    <th scope="col" className="py-2.5 px-3 text-sm font-bold text-ink whitespace-nowrap">Date</th>
                    <th scope="col" className="py-2.5 px-3 text-sm font-bold text-ink-2">Day</th>
                    <th scope="col" className="py-2.5 px-3 text-sm font-bold text-ink-2">Time</th>
                    <th scope="col" className="py-2.5 px-3 text-sm font-bold text-ink-2">Marks</th>
                    <th scope="col" className="py-2.5 px-3 text-center text-sm font-bold text-ink-2">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-line">
                  {filteredPapers.map((paper, index) => {
                    const isSelected = selectedDay
                      ? paper.examDate === selectedDay
                      : selectedPaper?.uuid === paper.uuid;
                    const tone = toneBySubject.get(paper.subject.uuid) ?? SUBJECT_TONES[0];
                    const Icon = subjectIcon(paper.subject.name);
                    return (
                      <tr
                        key={paper.uuid}
                        onClick={() => {
                          setSelectedUuid(paper.uuid);
                          setSelectedDay(null);
                        }}
                        className={cn(
                          'transition-colors cursor-pointer',
                          isSelected ? 'bg-accent-soft' : 'hover:bg-surface-2',
                        )}
                      >
                        <td className="py-3 px-3 text-center text-xs font-semibold text-ink-3">{index + 1}</td>
                        <td className="py-3 px-3 font-bold text-sm text-ink whitespace-nowrap">{paper.name}</td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border',
                              tone.badge,
                            )}
                          >
                            <Icon className={cn('h-3.5 w-3.5', tone.icon)} aria-hidden />
                            <span>{paper.subject.name}</span>
                          </span>
                        </td>
                        <td className="py-3 px-3 font-semibold text-sm text-ink whitespace-nowrap">
                          {formatDate(paper.examDate)}
                        </td>
                        <td className="py-3 px-3 text-sm font-medium text-ink-2 whitespace-nowrap">
                          {weekdayShort(paper.examDate)}
                        </td>
                        <td className="py-3 px-3 text-sm font-medium text-ink-2 whitespace-nowrap">
                          {paperTime(paper)}
                          {showSession && paperSession(paper) && (
                            <span className="block text-xs font-semibold text-ink-3">{paperSession(paper)}</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-sm font-medium text-ink-2 whitespace-nowrap">{paper.maxMarks}</td>
                        <td className="py-3 px-3 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setDetailPaper(paper)}
                            className="px-3 py-1 rounded-lg text-sm font-bold bg-accent-soft text-accent border border-accent-line hover:opacity-90 transition-opacity cursor-pointer"
                            aria-label={`View ${paper.name} ${paper.subject.name}`}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* --- RIGHT COLUMN: EXAM CALENDAR & NEXT EXAM --- */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-3.5">
            <div className="flex items-center justify-between pb-3 border-b border-line">
              <h3 className="text-base font-bold text-ink">Exam Calendar</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => shiftMonth(-1)}
                  className="p-1 rounded-lg text-ink-3 hover:text-ink hover:bg-surface-2 transition-colors cursor-pointer"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs font-bold text-ink" aria-live="polite">{monthLabel}</span>
                <button
                  type="button"
                  onClick={() => shiftMonth(1)}
                  className="p-1 rounded-lg text-ink-3 hover:text-ink hover:bg-surface-2 transition-colors cursor-pointer"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-2xs font-bold text-ink-3 uppercase py-1" aria-hidden>
              {WEEKDAY_SHORT.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2 text-center text-xs">
              {calendarDays.map(({ day, inMonth }) => {
                const dayPapers = papersByDay.get(day);
                const isExam = inMonth && Boolean(dayPapers);
                const isSelected = isExam && selectedPaper?.examDate === day;
                const isToday = inMonth && day === today;
                return (
                  <button
                    key={day}
                    type="button"
                    disabled={!isExam}
                    onClick={() => {
                      if (!dayPapers) return;
                      setSelectedUuid(dayPapers[0].uuid);
                      setSelectedDay(day);
                    }}
                    aria-label={
                      isExam
                        ? `${formatDate(day)}: ${dayPapers?.map((paper) => paper.subject.name).join(', ')}`
                        : formatDate(day)
                    }
                    className={cn(
                      'h-7 w-7 mx-auto flex items-center justify-center rounded-full text-xs transition-all select-none',
                      !inMonth && 'text-ink-3 opacity-30',
                      inMonth && !isExam && 'text-ink font-medium cursor-default',
                      isToday && !isExam && 'bg-surface-2 font-bold',
                      isExam && !isSelected && 'text-accent font-bold ring-2 ring-accent hover:bg-accent-soft cursor-pointer',
                      isSelected && 'bg-accent text-white font-black ring-2 ring-accent ring-offset-1 ring-offset-surface cursor-pointer',
                    )}
                  >
                    {Number(day.slice(8, 10))}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-center gap-4 pt-3 border-t border-line text-xs font-medium text-ink-2">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-accent" />
                <span>Exam Date</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-slate-400 dark:bg-slate-200" />
                <span>Today</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full border-2 border-accent" />
                <span>Selected</span>
              </div>
            </div>
          </div>

          {/* NEXT / SELECTED EXAM */}
          {selectedPaper && (
            <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-4">
              <div className="flex items-center justify-between pb-3 border-b border-line">
                <h3 className="text-base font-bold text-ink">
                  {selectedPaper.uuid === nextPaper?.uuid ? 'Next Exam' : 'Selected Exam'}
                </h3>
                <span className="text-xs font-bold text-accent bg-accent-soft border border-accent-line px-2.5 py-0.5 rounded-full">
                  {daysLeftLabel(daysBetween(today, selectedPaper.examDate))}
                </span>
              </div>

              <button
                type="button"
                onClick={() => setDetailPaper(selectedPaper)}
                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-surface-2 border border-line hover:border-accent-line transition-colors cursor-pointer text-left"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'h-10 w-10 rounded-xl flex items-center justify-center border shrink-0',
                      selectedTone?.badge,
                    )}
                  >
                    <SelectedIcon className={cn('h-5 w-5', selectedTone?.icon)} aria-hidden />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-ink">{selectedPaper.name}</h4>
                    <p className="text-xs font-semibold text-ink-2">{selectedPaper.subject.name}</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-ink-3 shrink-0" aria-hidden />
              </button>

              <div className="flex flex-col gap-2.5 text-xs text-ink-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-accent shrink-0" aria-hidden />
                  <span className="font-semibold text-ink">{formatDate(selectedPaper.examDate)}</span>
                  <span className="text-ink-3">({weekdayLong(selectedPaper.examDate)})</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-accent shrink-0" aria-hidden />
                  <span>{paperTime(selectedPaper)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-accent shrink-0" aria-hidden />
                  <span>
                    {EXAM_TYPE_LABEL[selectedPaper.examType]} · out of {selectedPaper.maxMarks} marks
                  </span>
                </div>
              </div>

              <Button type="button" onClick={() => setDetailPaper(selectedPaper)} className="w-full">
                View Details
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* 3. BOTTOM BANNER: PREPARATION TIPS & MOTIVATIONAL ART */}
      <div className="rounded-2xl border border-line bg-surface p-5 sm:p-6 shadow-2xs flex flex-col md:flex-row items-center justify-between gap-6">
        {/* Left Side: Tips Content */}
        <div className="flex items-start gap-4 flex-1">
          <div className="h-11 w-11 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-600 dark:text-purple-400 shrink-0">
            <Lightbulb className="h-6 w-6" />
          </div>
          <div className="flex flex-col gap-2.5">
            <h3 className="text-base font-bold text-ink">Preparation Tips</h3>
            <ul className="space-y-1.5 text-sm text-ink-2">
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                <span>Revise a little every day.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                <span>Use previous worksheets and class notes.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                <span>Make a study timetable and stick to it.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                <span>Ensure proper sleep and healthy diet.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Side: Graphic Illustration matching screenshot */}
        <div className="flex items-center gap-4 shrink-0 sm:pr-4">
          <div className="relative w-36 h-28 flex items-center justify-center">
            <svg
              viewBox="0 0 160 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full drop-shadow-md select-none"
            >
              <defs>
                <linearGradient id="bookBottom" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4338ca" />
                  <stop offset="100%" stopColor="#312e81" />
                </linearGradient>
                <linearGradient id="bookMid" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#4f46e5" />
                </linearGradient>
                <linearGradient id="bookTop" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#818cf8" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
                <linearGradient id="clockGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#38bdf8" />
                  <stop offset="100%" stopColor="#0284c7" />
                </linearGradient>
              </defs>

              {/* Bottom Book */}
              <polygon points="15,95 105,95 125,75 35,75" fill="url(#bookBottom)" />
              <polygon points="15,95 105,95 105,108 15,108" fill="#312e81" />
              <polygon points="105,95 125,75 125,88 105,108" fill="#e0e7ff" />

              {/* Middle Book */}
              <polygon points="20,78 100,78 118,60 38,60" fill="url(#bookMid)" />
              <polygon points="20,78 100,78 100,89 20,89" fill="#4338ca" />
              <polygon points="100,78 118,60 118,71 100,89" fill="#e0e7ff" />

              {/* Top Book */}
              <polygon points="25,62 95,62 112,46 42,46" fill="url(#bookTop)" />
              <polygon points="25,62 95,62 95,72 25,72" fill="#4f46e5" />
              <polygon points="95,62 112,46 112,56 95,72" fill="#e0e7ff" />

              {/* Clock next to books */}
              <circle
                cx="128"
                cy="46"
                r="18"
                fill="url(#clockGrad)"
                stroke="#bae6fd"
                strokeWidth="2.5"
              />
              <circle cx="128" cy="46" r="14" fill="#ffffff" />
              <circle cx="128" cy="46" r="1.5" fill="#0f172a" />
              <line
                x1="128"
                y1="46"
                x2="128"
                y2="37"
                stroke="#0f172a"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <line
                x1="128"
                y1="46"
                x2="135"
                y2="46"
                stroke="#0f172a"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </div>

          {/* Text Badge */}
          <div className="flex flex-col items-start leading-tight">
            <span className="text-sm sm:text-base font-extrabold italic tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-600 dark:from-purple-400 dark:via-indigo-300 dark:to-cyan-300 font-serif">
              Small Steps
            </span>
            <span className="text-base sm:text-lg font-black italic tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-300 dark:via-purple-300 dark:to-pink-300 font-serif">
              Big Results!
            </span>
          </div>
        </div>
      </div>


      {/* 4. PAPER DETAIL */}
      <Modal
        isOpen={detailPaper !== null}
        onClose={() => setDetailPaper(null)}
        title={detailPaper ? `${detailPaper.name} · ${detailPaper.subject.name}` : ''}
        size="sm"
        footer={
          <Button variant="secondary" onClick={() => setDetailPaper(null)}>
            Close
          </Button>
        }
      >
        {detailPaper && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="lbl">Date</dt>
            <dd className="text-ink font-semibold">
              {formatDate(detailPaper.examDate)} ({weekdayLong(detailPaper.examDate)})
            </dd>
            <dt className="lbl">Time</dt>
            <dd className="text-ink">{paperTime(detailPaper)}</dd>
            <dt className="lbl">Type</dt>
            <dd className="text-ink">{EXAM_TYPE_LABEL[detailPaper.examType]}</dd>
            <dt className="lbl">Out of</dt>
            <dd className="text-ink">{detailPaper.maxMarks} marks</dd>
            <dt className="lbl">Days left</dt>
            <dd className="text-ink">{daysLeftLabel(daysBetween(today, detailPaper.examDate))}</dd>
          </dl>
        )}
      </Modal>
    </div>
  );
}

interface StatTileProps {
  title: string;
  value: string;
  caption: string;
  icon: LucideIcon;
  frame: string;
  titleClass: string;
  valueClass: string;
  captionClass: string;
  /** The solid chip behind the icon. */
  iconClass: string;
}

/** One of the four tiles across the top, drawn as the Results tab draws its own. */
function StatTile({ title, value, caption, icon: Icon, frame, titleClass, valueClass, captionClass, iconClass }: StatTileProps) {
  return (
    <div className={cn('relative block overflow-hidden rounded-2xl sm:rounded-3xl border p-3.5 sm:p-5 shadow-2xs', frame)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className={cn('text-sm font-bold leading-tight', titleClass)}>{title}</p>
          <p className={cn('mt-0.5 text-2xl font-black tracking-tight tabular-nums truncate', valueClass)}>{value}</p>
          <p className={cn('mt-0.5 sm:mt-1 text-xs font-semibold', captionClass)}>{caption}</p>
        </div>
        <span
          className={cn(
            'grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl text-white',
            iconClass,
          )}
          aria-hidden
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}
